import { SxProps, Theme } from "@mui/material";
import { brandAlpha } from "../../../theme/mixins";

/**
 * The editor is drawn as nested cards rather than as one long form: four levels of
 * indentation is more than a flat list can carry, and the read view already teaches the
 * reader that a direction contains sections which contain services.
 */
export const editorWrapperStyles: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
};

export const directionCardStyles: SxProps<Theme> = {
    p: 2.5,
    borderRadius: 3,
    border: '1px solid rgba(16,24,40,0.10)',
};

// Each level steps in a little and picks up its own tint, so depth is readable at a
// glance without a rule or a bullet.
export const sectionCardStyles: SxProps<Theme> = {
    p: 2,
    mt: 2,
    ml: { xs: 0, sm: 2 },
    borderRadius: 2,
    backgroundColor: brandAlpha('primary', 0.03),
    border: '1px solid rgba(16,24,40,0.08)',
};

export const typeCardStyles: SxProps<Theme> = {
    p: 2,
    mt: 1.5,
    ml: { xs: 0, sm: 2 },
    borderRadius: 2,
    backgroundColor: 'background.paper',
    border: '1px solid rgba(16,24,40,0.08)',
};

/** Label, the two language fields, then the row's own buttons. */
export const nodeHeaderStyles: SxProps<Theme> = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: 1.5,
};

export const nodeLabelStyles: SxProps<Theme> = {
    minWidth: 92,
    pt: 2,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    fontSize: '0.72rem',
    color: 'text.secondary',
};

export const nodeFieldsStyles: SxProps<Theme> = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 1.5,
    flex: 1,
    minWidth: 240,
};

export const textFieldStyles: SxProps<Theme> = {
    flex: 1,
    minWidth: 180,
};

export const priceFieldStyles: SxProps<Theme> = {
    width: { xs: '100%', sm: 150 },
};

/**
 * Minutes and sessions. Narrower than the price, because both hold two digits in
 * practice, and full width on a phone so the row stacks rather than crushing.
 */
export const optionNumberFieldStyles: SxProps<Theme> = {
    width: { xs: '100%', sm: 110 },
};

/**
 * Session or plan. Sits where it decides what follows it, because the two controls to its
 * right are different questions depending on which way it is set.
 */
export const kindToggleStyles: SxProps<Theme> = {
    alignSelf: 'flex-start',
    mt: 0.5,
};

/**
 * Which session a plan is a block of. As wide as the free-text fields rather than as narrow
 * as the numbers: it holds a discipline and a line, not two digits.
 */
export const basedOnFieldStyles: SxProps<Theme> = {
    flex: 1,
    minWidth: 220,
};

export const rowActionsStyles: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    pt: 1,
};

/** Option rows sit close together: they are one line of a price list, not a card. */
export const optionRowStyles: SxProps<Theme> = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: 1.5,
    mt: 1.5,
    ml: { xs: 0, sm: 2 },
};

export const addButtonStyles: SxProps<Theme> = {
    mt: 1.5,
    textTransform: 'none',
    alignSelf: 'flex-start',
};

export const addDirectionButtonStyles: SxProps<Theme> = {
    textTransform: 'none',
    alignSelf: 'flex-start',
};

export const emptyEditorStyles: SxProps<Theme> = {
    py: 4,
    textAlign: 'center',
    color: 'text.secondary',
};
